import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown, Network, Wallet, TrendingUp } from "lucide-react";
import { api, mediaUrl } from "../lib/api";
import ProductCard from "../components/ProductCard";

const CATEGORY_TILES = [
  { name: "Electronics", span: "md:col-span-5", img: "https://images.unsplash.com/photo-1760587162690-95608c8ab2da?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHw0fHxwcmVtaXVtJTIwZWxlY3Ryb25pYyUyMHByb2R1Y3RzJTIwY2xlYW4lMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc5MzUzNTYzNnww&ixlib=rb-4.1.0&q=85" },
  { name: "Fashion", span: "md:col-span-4", img: "https://images.unsplash.com/photo-1618560550792-7f042c3f5611?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHw0fHxpbmRpYW4lMjBmYXNoaW9uJTIwbGlmZXN0eWxlJTIwbW9kZWx8ZW58MHx8fHwxNzkzNTM1NjM2fDA&ixlib=rb-4.1.0&q=85" },
  { name: "Beauty", span: "md:col-span-3", img: "https://images.unsplash.com/photo-1598528738936-c50861cc75a9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBiZWF1dHklMjBjb3NtZXRpY3MlMjBtaW5pbWFsaXN0fGVufDB8fHx8MTc5MzUzNTYzNnww&ixlib=rb-4.1.0&q=85" },
];

const DEFAULTS = {
  hero_kicker: "Vearn E-Commerce · Coimbatore",
  hero_line1: "SHOP.",
  hero_line2: "EARN.",
  hero_line3: "GROW TOGETHER.",
  hero_sub: "Premium products delivered across India — and a Partner Program where every purchase can open the door to Direct and Binary income.",
  hero_image: "https://images.unsplash.com/photo-1622049605334-72e1e4432346?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxpbmRpYW4lMjBmYXNoaW9uJTIwbGlmZXN0eWxlJTIwbW9kZWx8ZW58MHx8fHwxNzkzNTM1NjM2fDA&ixlib=rb-4.1.0&q=85",
  hero_video: "",
  cta_primary: "Shop the Collection",
  cta_secondary: "Become a Partner",
  marquee_words: "SHOP,EARN,GROW TOGETHER,VEARN",
  partner_heading: "One purchase. One Partner ID. A network that pays.",
  partner_sub: "Buy the Partner Starter Kit once, receive your unique Partner ID and referral code, and grow a team. Income is earned on qualifying purchases — never for recruitment alone.",
};

const MaskedLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

function Marquee({ words }) {
  const items = words.split(",").map((w) => w.trim()).filter(Boolean);
  const row = items.length ? items : ["VEARN"];
  return (
    <div className="overflow-hidden py-10 border-y border-brand-ink/5 select-none" aria-hidden>
      <div className="flex w-max animate-marquee gap-12">
        {[...row, ...row, ...row, ...row].map((t, i) => (
          <span key={i} className="flex items-center gap-12 font-display text-5xl md:text-6xl font-extrabold tracking-tight brand-gradient-text whitespace-nowrap">
            {t} <span className="h-2 w-2 rounded-full brand-gradient-bg inline-block" />
          </span>
        ))}
      </div>
    </div>
  );
}

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [newest, setNewest] = useState([]);
  const [content, setContent] = useState(DEFAULTS);
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 600], [0, 120]);

  useEffect(() => {
    api.get("/content/home").then((r) => setContent({ ...DEFAULTS, ...r.data })).catch(() => {});
    api.get("/products", { params: { featured: true, limit: 4 } }).then((r) => setFeatured(r.data.products)).catch(() => {});
    api.get("/products", { params: { sort: "newest", limit: 4 } }).then((r) => setNewest(r.data.products)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <section className="relative min-h-[92vh] flex items-end overflow-hidden bg-brand-navy">
        <motion.div style={{ y: heroY }} className="absolute inset-0">
          {content.hero_video ? (
            <video src={mediaUrl(content.hero_video)} poster={mediaUrl(content.hero_image) || undefined} autoPlay muted loop playsInline aria-label="Vearn home banner video" className="h-full w-full object-cover opacity-90" data-testid="hero-video" />
          ) : (
            <img src={mediaUrl(content.hero_image)} alt="Vearn hero banner" className="h-full w-full object-cover opacity-90" data-testid="hero-image" />
          )}
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-brand-navy via-brand-navy/45 to-brand-navy/20" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pb-24 pt-40 w-full">
          <p className="text-xs uppercase tracking-[0.3em] text-brand-cyan font-semibold mb-6">
            <MaskedLine delay={0.1}>{content.hero_kicker}</MaskedLine>
          </p>
          <h1 className="font-display font-extrabold tracking-tight text-white text-5xl sm:text-6xl lg:text-7xl leading-[0.95]">
            <MaskedLine delay={0.2}>{content.hero_line1}</MaskedLine>
            <MaskedLine delay={0.35}><span className="brand-gradient-text">{content.hero_line2}</span></MaskedLine>
            <MaskedLine delay={0.5}>{content.hero_line3}</MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8, duration: 0.7 }}
            className="mt-6 max-w-xl text-white/70 text-base md:text-lg leading-relaxed"
            data-testid="hero-sub"
          >
            {content.hero_sub}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.7 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Link to="/shop" data-testid="hero-shop-button"
              className="group inline-flex items-center gap-2 rounded-full brand-gradient-bg px-8 h-13 py-4 text-white font-semibold text-sm shadow-[0_8px_30px_rgba(0,229,255,0.35)] hover:scale-[1.02] transition-transform">
              {content.cta_primary} <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/partner" data-testid="hero-partner-button"
              className="inline-flex items-center gap-2 rounded-full border border-white/30 px-8 py-4 text-white font-semibold text-sm backdrop-blur-sm hover:bg-white/10 transition-colors">
              {content.cta_secondary}
            </Link>
          </motion.div>
        </div>
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2 }}
          className="absolute bottom-6 right-8 hidden md:flex h-11 w-11 rounded-full border border-white/25 items-center justify-center">
          <ArrowDown className="h-4 w-4 text-white/70" />
        </motion.div>
      </section>

      <Marquee words={content.marquee_words} />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-24" data-testid="categories-section">
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-brand-blue font-semibold">Curated for you</p>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight mt-3">Shop by category</h2>
          </div>
          <Link to="/shop" className="hidden md:inline-flex items-center gap-2 text-sm font-semibold text-brand-blue hover:gap-3 transition-all">
            View all <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {CATEGORY_TILES.map((c, i) => (
            <motion.div key={c.name} className={c.span}
              initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.7, delay: i * 0.12 }}>
              <Link to={`/shop?category=${c.name}`} data-testid={`category-tile-${c.name.toLowerCase()}`}
                className="group relative block h-72 md:h-96 overflow-hidden rounded-3xl">
                <img src={c.img} alt={c.name} loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/80 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 flex items-center gap-3">
                  <span className="font-display text-2xl font-bold text-white">{c.name}</span>
                  <span className="h-9 w-9 rounded-full bg-white/15 backdrop-blur flex items-center justify-center group-hover:bg-brand-cyan transition-colors">
                    <ArrowRight className="h-4 w-4 text-white group-hover:text-brand-navy" />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-24" data-testid="featured-section">
        <div className="flex items-end justify-between mb-12">
          <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight">Featured products</h2>
          <Link to="/shop" className="hidden md:inline-flex items-center gap-2 text-sm font-semibold text-brand-blue hover:gap-3 transition-all">
            Shop all <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {featured.map((p, i) => <ProductCard key={p.id} product={p} tall={i % 2 === 1} />)}
        </div>
      </section>

      {false && <section className="bg-brand-navy text-white relative overflow-hidden" data-testid="partner-band">
        <div className="absolute -top-40 -right-40 h-96 w-96 rounded-full bg-brand-blue/20 blur-[120px]" />
        <div className="absolute -bottom-40 -left-40 h-96 w-96 rounded-full bg-brand-cyan/10 blur-[120px]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-24 md:py-32">
          <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
            <p className="text-xs uppercase tracking-[0.3em] text-brand-cyan font-semibold">The Vearn Partner Program</p>
            <h2 className="font-display text-4xl md:text-6xl font-bold tracking-tight mt-4 max-w-3xl leading-[1.05]" data-testid="partner-heading">
              {content.partner_heading}
            </h2>
            <p className="mt-6 max-w-2xl text-white/60 leading-relaxed">
              {content.partner_sub}
            </p>
          </motion.div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Network, value: "₹450", label: "Direct income", desc: "Earned when someone you refer activates as a Partner with a qualifying purchase." },
              { icon: TrendingUp, value: "₹300", label: "Per matched binary pair", desc: "Left and right team volumes match into pairs as your network grows." },
              { icon: Wallet, value: "₹6,000", label: "Daily binary ceiling", desc: "Up to 20 paid pairs per day, with team volume carrying forward." },
            ].map((s, i) => (
              <motion.div key={s.label}
                initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.12 }}
                className="rounded-2xl border border-white/10 bg-brand-surface/60 p-7 hover:-translate-y-1 hover:border-brand-cyan/40 transition-all duration-300">
                <s.icon className="h-6 w-6 text-brand-cyan" />
                <p className="font-display text-4xl font-bold mt-5">{s.value}</p>
                <p className="text-sm font-semibold text-brand-cyan mt-1 uppercase tracking-wider">{s.label}</p>
                <p className="text-sm text-white/50 mt-3 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.4 }} className="mt-12">
            <Link to="/partner" data-testid="partner-band-cta"
              className="inline-flex items-center gap-2 rounded-full brand-gradient-bg px-8 py-4 text-white font-semibold text-sm shadow-[0_8px_30px_rgba(41,98,255,0.4)] hover:scale-[1.02] transition-transform">
              Explore the Partner Program <ArrowRight className="h-4 w-4" />
            </Link>
          </motion.div>
        </div>
      </section>}

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-24" data-testid="new-arrivals-section">
        <div className="flex items-end justify-between mb-12">
          <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight">New arrivals</h2>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {newest.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>
    </div>
  );
}
